import { Component, ReactNode } from 'react';
import { ChunkLoadingFallback } from './ChunkLoadingFallback';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { captureError } from '@/services/errorCapture';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('[RouteErrorBoundary]', error, errorInfo);
    
    // Log chunk loading errors specifically
    if (this.isChunkLoadError(error)) {
      console.warn('[RouteErrorBoundary] Chunk loading error detected:', {
        message: error.message,
        retryCount: this.state.retryCount
      });
      
      // Capture chunk loading error to backend
      captureError(error, errorInfo, 'chunk_load');
    } else {
      // Capture all other route errors to backend
      captureError(error, errorInfo, 'runtime');
    }
  }

  isChunkLoadError(error: Error): boolean {
    return error.message?.includes('Failed to fetch dynamically imported module') ||
           error.message?.includes('Importing a module script failed') ||
           error.message?.includes('error loading dynamically imported module') ||
           error.message?.includes('Loading chunk');
  }

  handleRetry = () => {
    const { retryCount } = this.state;
    
    // Circuit breaker: Check session-based reload tracking to prevent infinite loops
    const sessionRetries = parseInt(sessionStorage.getItem('route_error_retries') || '0', 10);
    
    if (sessionRetries >= 2) {
      console.warn('[RouteErrorBoundary] Max session retries reached, stopping reload loop');
      // Don't reload, just show error state
      return;
    }
    
    // If retry count is high, force a full reload with session tracking
    if (retryCount >= 2) {
      sessionStorage.setItem('route_error_retries', String(sessionRetries + 1));
      window.location.reload();
      return;
    }
    
    // Reset error state to retry
    this.setState({ 
      hasError: false, 
      error: null, 
      retryCount: retryCount + 1 
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use specialized handler for chunk loading errors
      if (this.isChunkLoadError(this.state.error!)) {
        return <ChunkLoadingFallback error={this.state.error!} onRetry={this.handleRetry} />;
      }

      // Generic error fallback
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription className="space-y-4">
              <p>We encountered an error loading this page.</p>
              <p className="text-sm text-muted-foreground">
                {this.state.error?.message}
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={this.handleRetry}
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
                <Button 
                  onClick={() => {
                    window.location.href = '/dashboard';
                  }} 
                  className="flex-1"
                >
                  Go to Dashboard
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
